"use client";

import React from "react";

import Image, { type ImageLoaderProps } from "next/image";

const POINTER_INTENT_DELAY_MS = 80;

export interface ProductImageSource {
  primary: string;
  fallback?: string;
}

export interface ProductImageLoadInfo {
  currentSrc: string;
  fallbackUsed: boolean;
  index: number;
  naturalHeight: number;
  naturalWidth: number;
  renderedWidth: number;
}

export interface ProductImagePresentationInfo extends ProductImageLoadInfo {
  presentedAt: number;
  timingSource: "decode_frame";
}

export interface ProductImageErrorInfo {
  index: number;
}

export type ProductImageLoadingPolicy = "eager-adjacent" | "intent-adjacent";

export interface ProductImageCarouselProps {
  images: Array<string | ProductImageSource>;
  alt: string;
  objectFit?: "cover" | "contain";
  className?: string;
  imageClassName?: string;
  loader?: (resolverProps: ImageLoaderProps) => string;
  unoptimized?: boolean;
  priorityFirst?: boolean;
  sizes?: string;
  quality?: number;
  loadingPolicy?: ProductImageLoadingPolicy;
  elementTiming?: string;
  onImageLoad?: (img: ProductImageLoadInfo) => void;
  onImagePresented?: (img: ProductImagePresentationInfo) => void;
  onImageError?: (error: ProductImageErrorInfo) => void;
}

function normalizeImage(image: string | ProductImageSource): ProductImageSource | null {
  if (typeof image === "string") {
    return image ? { primary: image } : null;
  }
  return image.primary ? image : null;
}

function dedupeImageSources(sources: ProductImageSource[]): ProductImageSource[] {
  return [
    ...new Map(
      sources.map((source) => [`${source.primary}::${source.fallback ?? ""}`, source])
    ).values(),
  ];
}

function retainMountedReadyImages(
  readyImages: Set<number>,
  mountedImages: Set<number>,
  nextMountedImages: Set<number>
): Set<number> {
  return new Set(
    [...readyImages].filter(
      (readyIndex) => mountedImages.has(readyIndex) && nextMountedImages.has(readyIndex)
    )
  );
}

function CarouselArrow({
  direction,
  onClick,
  onIntent,
  className = "",
}: {
  direction: "prev" | "next";
  onClick: (event: React.MouseEvent) => void;
  onIntent?: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={direction === "prev" ? "Previous image" : "Next image"}
      className={`flex h-10 w-8 items-center justify-center rounded-md bg-white text-gray-900 shadow-md transition-all duration-200 hover:bg-white hover:shadow-lg ${className}`}
      onFocus={onIntent}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onClick(event);
      }}
    >
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        {direction === "prev" ? (
          <path
            d="M12.5 4L6.5 10L12.5 16"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <path
            d="M7.5 4L13.5 10L7.5 16"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
    </button>
  );
}

export function ProductImageCarousel({
  images,
  alt,
  objectFit = "cover",
  className = "",
  imageClassName = "",
  loader,
  unoptimized,
  priorityFirst = false,
  sizes = "(max-width: 640px) 136px, (max-width: 1024px) 152px, 172px",
  quality = 70,
  loadingPolicy = "eager-adjacent",
  elementTiming,
  onImageLoad,
  onImagePresented,
  onImageError,
}: ProductImageCarouselProps) {
  const objectFitClass = objectFit === "contain" ? "object-contain" : "object-cover";
  const imageSources = React.useMemo(
    () =>
      dedupeImageSources(
        images.map(normalizeImage).filter((image): image is ProductImageSource => image !== null)
      ),
    [images]
  );
  const [selectedImageIndex, setSelectedImageIndex] = React.useState(0);
  const [loadedImages, setLoadedImages] = React.useState<Set<number>>(new Set([0]));
  const [readyImages, setReadyImages] = React.useState<Set<number>>(new Set());
  const [fallbackImageIndexes, setFallbackImageIndexes] = React.useState<Set<number>>(new Set());
  const [pendingImageIndex, setPendingImageIndex] = React.useState<number | null>(null);
  const pointerIntentTimeoutRef = React.useRef<number | null>(null);
  const touchStartXRef = React.useRef<number | null>(null);

  const cancelPointerIntent = React.useCallback(() => {
    if (pointerIntentTimeoutRef.current === null) return;
    window.clearTimeout(pointerIntentTimeoutRef.current);
    pointerIntentTimeoutRef.current = null;
  }, []);

  React.useEffect(() => {
    cancelPointerIntent();
    setSelectedImageIndex(0);
    setLoadedImages(new Set([0]));
    setReadyImages(new Set());
    setFallbackImageIndexes(new Set());
    setPendingImageIndex(null);
  }, [cancelPointerIntent, imageSources]);

  React.useEffect(() => {
    if (loadingPolicy !== "eager-adjacent" || imageSources.length <= 1) return;
    const nextIndex = (selectedImageIndex + 1) % imageSources.length;
    const prevIndex = selectedImageIndex === 0 ? imageSources.length - 1 : selectedImageIndex - 1;
    setLoadedImages((prev) => new Set([...prev, nextIndex, prevIndex]));
  }, [imageSources.length, loadingPolicy, selectedImageIndex]);

  const prepareImage = React.useCallback(
    (index: number) => {
      if (
        loadingPolicy !== "intent-adjacent" ||
        imageSources.length <= 1 ||
        pendingImageIndex !== null
      ) {
        return;
      }
      const next = new Set([selectedImageIndex, index]);
      if (
        loadedImages.size === 2 &&
        loadedImages.has(selectedImageIndex) &&
        loadedImages.has(index)
      ) {
        return;
      }
      setReadyImages((current) => retainMountedReadyImages(current, loadedImages, next));
      setLoadedImages(next);
    },
    [imageSources.length, loadedImages, loadingPolicy, pendingImageIndex, selectedImageIndex]
  );

  const previousImageIndex =
    selectedImageIndex === 0 ? imageSources.length - 1 : selectedImageIndex - 1;
  const nextImageIndex = (selectedImageIndex + 1) % imageSources.length;

  const handlePointerEnter = React.useCallback(() => {
    if (loadingPolicy !== "intent-adjacent" || pointerIntentTimeoutRef.current !== null) return;
    pointerIntentTimeoutRef.current = window.setTimeout(() => {
      pointerIntentTimeoutRef.current = null;
      prepareImage(nextImageIndex);
    }, POINTER_INTENT_DELAY_MS);
  }, [loadingPolicy, nextImageIndex, prepareImage]);

  React.useEffect(() => cancelPointerIntent, [cancelPointerIntent]);

  const changeImage = React.useCallback(
    (newIndex: number) => {
      if (newIndex === selectedImageIndex) return;

      if (loadingPolicy === "intent-adjacent") {
        if (loadedImages.has(newIndex) && readyImages.has(newIndex)) {
          setSelectedImageIndex(newIndex);
          setLoadedImages(new Set([newIndex]));
          setReadyImages(new Set([newIndex]));
          setPendingImageIndex(null);
          return;
        }

        // Keep the current image visible until the requested target has loaded.
        // The target image's load handler completes the selection and releases
        // the previous decoded image.
        const next = new Set([selectedImageIndex, newIndex]);
        setReadyImages((current) => retainMountedReadyImages(current, loadedImages, next));
        setLoadedImages(next);
        setPendingImageIndex(newIndex);
        return;
      }

      setLoadedImages((current) => new Set([...current, newIndex]));
      setSelectedImageIndex(newIndex);
    },
    [loadedImages, loadingPolicy, readyImages, selectedImageIndex]
  );

  const handlePrevClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    cancelPointerIntent();
    changeImage(previousImageIndex);
  };

  const handleNextClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    cancelPointerIntent();
    changeImage(nextImageIndex);
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartXRef.current = event.touches[0].clientX;
    cancelPointerIntent();
    prepareImage(nextImageIndex);
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStartXRef.current === null) return;
    const deltaX = event.changedTouches[0].clientX - touchStartXRef.current;
    if (Math.abs(deltaX) > 40 && imageSources.length > 1) {
      if (deltaX < 0) {
        changeImage(nextImageIndex);
      } else {
        changeImage(previousImageIndex);
      }
    }
    touchStartXRef.current = null;
  };

  if (imageSources.length === 0) {
    return null;
  }

  return (
    <div
      className={`${className} group touch-pan-y`}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={cancelPointerIntent}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {imageSources.map((imageSource, index) => {
        if (!loadedImages.has(index)) return null;
        const imageUrl =
          fallbackImageIndexes.has(index) && imageSource.fallback
            ? imageSource.fallback
            : imageSource.primary;
        const elementTimingAttributes =
          index === 0 && elementTiming ? { elementtiming: elementTiming } : {};

        return (
          <Image
            {...elementTimingAttributes}
            key={`${imageSource.primary}::${imageSource.fallback ?? ""}`}
            src={imageUrl}
            alt={`${alt} - Image ${index + 1}`}
            loader={loader}
            fill
            sizes={sizes}
            quality={quality}
            priority={priorityFirst && index === 0}
            unoptimized={unoptimized}
            data-image-fallback={fallbackImageIndexes.has(index) ? "true" : "false"}
            className={`transition-opacity duration-300 ease-in-out ${objectFitClass} ${imageClassName}`}
            style={{
              opacity: selectedImageIndex === index ? 1 : 0,
              zIndex: selectedImageIndex === index ? 2 : 1,
              pointerEvents: selectedImageIndex === index ? "auto" : "none",
            }}
            fetchPriority={priorityFirst && index === 0 ? "high" : undefined}
            onLoad={(event) => {
              const image = event.currentTarget;
              if (pendingImageIndex === index) {
                setSelectedImageIndex(index);
                setLoadedImages(new Set([index]));
                setReadyImages(new Set([index]));
                setPendingImageIndex(null);
              } else {
                setReadyImages((current) => new Set([...current, index]));
              }
              const loadInfo = {
                currentSrc: image.currentSrc,
                fallbackUsed: fallbackImageIndexes.has(index),
                index,
                naturalWidth: image.naturalWidth,
                naturalHeight: image.naturalHeight,
                renderedWidth: image.getBoundingClientRect().width,
              };
              onImageLoad?.(loadInfo);
              if (!onImagePresented) return;

              // `load` only means the bytes are available. Wait for decode and
              // the following presentation frame before using this as the RUM
              // fallback. Element Timing, when available, normally wins during
              // the short grace period and supplies the actual render time.
              const decoded =
                typeof image.decode === "function"
                  ? image.decode().catch(() => {})
                  : Promise.resolve();
              void decoded.then(() => {
                window.requestAnimationFrame((presentedAt) => {
                  const emitFallback = () =>
                    onImagePresented({
                      ...loadInfo,
                      presentedAt,
                      timingSource: "decode_frame",
                    });
                  const supportsElementTiming =
                    Boolean(elementTiming) &&
                    typeof PerformanceObserver !== "undefined" &&
                    PerformanceObserver.supportedEntryTypes?.includes("element");
                  if (supportsElementTiming) window.setTimeout(emitFallback, 100);
                  else emitFallback();
                });
              });
            }}
            onError={() => {
              if (
                imageSource.fallback &&
                imageSource.fallback !== imageSource.primary &&
                !fallbackImageIndexes.has(index)
              ) {
                setFallbackImageIndexes((prev) => new Set([...prev, index]));
                return;
              }
              if (pendingImageIndex === index) {
                setPendingImageIndex(null);
                setLoadedImages(new Set([selectedImageIndex]));
                setReadyImages(
                  (current) =>
                    new Set([...current].filter((readyIndex) => readyIndex === selectedImageIndex))
                );
              }
              onImageError?.({ index });
            }}
          />
        );
      })}

      {imageSources.length > 1 && (
        <>
          <CarouselArrow
            direction="prev"
            onClick={handlePrevClick}
            onIntent={() => {
              cancelPointerIntent();
              prepareImage(previousImageIndex);
            }}
            className="absolute left-4 top-1/2 z-10 hidden -translate-y-1/2 opacity-0 group-hover:opacity-100 sm:flex"
          />
          <CarouselArrow
            direction="next"
            onClick={handleNextClick}
            onIntent={() => {
              cancelPointerIntent();
              prepareImage(nextImageIndex);
            }}
            className="absolute right-4 top-1/2 z-10 hidden -translate-y-1/2 opacity-0 group-hover:opacity-100 sm:flex"
          />
        </>
      )}
    </div>
  );
}
